import { Form, FormAssertive, FormSubmit, useForm } from "@/components/form"
import { Button } from "@/components/ui/button"
import { Dialog } from "@/components/ui/dialog"
import { UseDialogFormProps, UseDialogProps } from "@/components/ui/hooks/useDialog"
import { formatError } from "@/fns/format-error"
import { useMemoOnce } from "@/hooks/useMemoOnce"
import { resetAllStoresAndReload } from "@/store"
import { createInvoiceLine } from "@/store/invoices/actions"
import { match } from "ts-pattern"
import { FormValues, InvoiceLineForm, emptyLine, invoiceLineValidation } from "./InvoiceForm"

/**
 * CreateLineDialog
 */
export const CreateLineDialog: React.FC<UseDialogProps<string>> = ({ item, onOpenChange, open }) => {
  return (
    <Dialog {...{ open, onOpenChange }} title='Create a new product' className='max-w-2xl'>
      {item !== false && <DialogForm {...{ item, onOpenChange }} />}
    </Dialog>
  )
}

const DialogForm: React.FC<UseDialogFormProps<string>> = ({ item: invoiceId, onOpenChange }) => {
  const form = useForm<FormValues["linesById"][string]>({
    allowSubmitAttempt: true,
    allowErrorSubmit: true,
    values: useMemoOnce(() => emptyLine),
    validate: invoiceLineValidation,
    onSubmit: async ({ values }) => {
      if (!form.isValid && !form.attemptedSubmit) return formatError("VALIDATION_FAILURE")
      return match(await createInvoiceLine(invoiceId, values))
        .with({ error: false }, () => {
          toast.success("Product created")
          onOpenChange(false)
        })
        .otherwise(({ code }) =>
          match(code)
            .with("VALIDATION_FAILURE", formatError)
            .with("INVALID_AUTH_SESSION", resetAllStoresAndReload)
            .with("FETCH_ERROR", (code) => toast.error(formatError(code)))
        )
    },
  })

  return (
    <Form form={form} className='grid gap-6'>
      <FormAssertive />
      <InvoiceLineForm />
      <Dialog.Footer>
        <Dialog.Close asChild>
          <Button variant='secondary'>Back</Button>
        </Dialog.Close>
        <FormSubmit>{!form.isSubmitting && form.attemptedSubmit ? "Create despite errors" : "Create"}</FormSubmit>
      </Dialog.Footer>
    </Form>
  )
}
